import React, { useContext, useState } from 'react';
import { Upload, Progress, Typography, Modal, Alert, message } from 'antd';
import {
  InboxOutlined,
  UploadOutlined,
  CloseOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import FileSize from '../../../constants/FileSize';
import {
  BE_BASE_URL,
  BE_DOMAIN,
  LOCK_MSG,
} from '../../../constants/ApiConstants';
import CustomButton from '../Button/Button';
import { ActionContext } from '../Navigation/Navigation';

const { Dragger } = Upload;
const { Text, Title } = Typography;

const UploadFile = ({ visible, onClose, url, type, title }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploaded, setUploaded] = useState(false);
  const [error, setError] = useState(false);
  const [fileInfo, setFileInfo] = useState(null);
  const [cancelTokenSource, setCancelTokenSource] = useState(null);
  const { setAction } = useContext(ActionContext);
  let progressInterval = null;

  const startProgressSimulation = () => {
    setProgress(10);
    progressInterval = setInterval(() => {
      setProgress((prev) => (prev < 90 ? prev + 10 : prev));
    }, 500);
  };

  const handleBeforeUpload = (file) => {
    // Allow Excel only for DOTS Pre-Fail endpoint
    const allowExcel =
      typeof url === 'string' && url.endsWith('/payoutPreFail');
    const validMimeTypes = allowExcel
      ? [
          'text/csv',
          'application/vnd.ms-excel',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        ]
      : ['text/csv'];
    const isValidMime = validMimeTypes.includes(file.type);
    const isValidExt = allowExcel
      ? /\.(csv|xlsx|xls)$/i.test(file.name)
      : /\.(csv)$/i.test(file.name);
    const isAcceptedFormat = isValidMime || isValidExt;

    const isSizeValid =
      file.size / 1024 / 1024 <= FileSize.TRANSACTION_FILE_SIZE;

    if (!isAcceptedFormat) {
      setError(
        allowExcel
          ? 'Upload Failed. Please upload CSV or Excel file only.'
          : 'Upload Failed. Please upload csv file format only.',
      );
      return Upload.LIST_IGNORE;
    }

    if (!isSizeValid) {
      setError(
        `Upload Failed. File exceeds ${FileSize.TRANSACTION_FILE_SIZE} MB limit.`,
      );
      return Upload.LIST_IGNORE;
    }

    setFileInfo({
      name: file.name,
      size: (file.size / 1024 / 1024).toFixed(1) + 'MB',
    });
    uploadFile(file);
    return false;
  };

  const uploadFile = async (file) => {
    setUploading(true);
    setProgress(0);
    startProgressSimulation();
    setAction(true);
    const formData = new FormData();
    formData.append('file', file);

    const source = axios.CancelToken.source();
    setCancelTokenSource(source);

    try {
      await axios
        .post(BE_BASE_URL + url, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          cancelToken: source.token,
          withCredentials: true,
        })
        .then((res) => {
          clearInterval(progressInterval);
          setProgress(100);
          if (res.data === LOCK_MSG) {
            setUploading(false);
            setError(
              'System is Locked for any updates, Please try after some time',
            );
          } else {
            setTimeout(() => {
              setUploading(false);
              setUploaded(true);
            }, 1000);
          }
        })
        .catch((err) => {
          if (!axios.isCancel(err)) {
            if (err.response && err.response.status === 401) {
              message.error(
                'Your token expired we will try to refresh your screen.if we are unable to please login again. thanks..',
              );
              window.location.href = BE_DOMAIN + '/saml2/authenticate/azure';
            }
            const error = err?.response?.data;
            setUploading(false);
            setError(
              `Upload Failed. Unable to upload file due to ${error && error}`,
            );
            resetUpload();
          }
        });
    } catch {
      resetUpload(); // Ensure progress resets on unexpected errors
      setError('Upload Failed, Something went wrong.');
    } finally {
      setAction(false);
    }
  };

  const handleClose = () => {
    resetUpload();
    onClose();
  };
  const cancelUpload = () => {
    if (cancelTokenSource) {
      cancelTokenSource.cancel('Upload canceled by the user.');
    }
    resetUpload();
  };

  const resetUpload = () => {
    setUploading(false);
    setUploaded(false);
    setProgress(0);
    setFileInfo(null);
    setCancelTokenSource(null);
  };

  const handleCloseError = () => {
    setError(false);
  };

  return (
    <Modal
      title={title || 'Upload CSV File'}
      centered
      open={visible}
      onCancel={() => handleClose()}
      footer={null}
      width={703}
      height={500}
      className="upload-model"
      styles={{
        body: { padding: '61px' },
      }}
    >
      {!uploaded ? (
        <>
          <Dragger
            style={{ background: 'none', border: 'none' }}
            beforeUpload={handleBeforeUpload}
            showUploadList={false}
            multiple={false}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined style={{ color: '#08979C' }} />
            </p>
            <p style={{ fontSize: '20px', fontWeight: '400' }}>
              Click or drag a file to this area to upload
            </p>
            <p
              style={{
                color: '#08979C',
                fontSize: '14px',
                fontWeight: '400',
              }}
            >
              {typeof url === 'string' && url.endsWith('/payoutPreFail')
                ? 'Accepted file formats are .csv or .xlsx/.xls with a maximum size of 1 GB.'
                : 'Accepted file format is .csv only with a maximum size of 1 GB.'}
            </p>
          </Dragger>
          {uploading && (
            <div
              style={{
                textAlign: 'left',
                padding: '10px 145px',
                borderRadius: '8px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '8px',
                }}
              >
                <div
                  style={{
                    background: '#E6F7F7',
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <UploadOutlined
                    style={{ fontSize: '18px', color: '#008080' }}
                  />
                </div>
                <div style={{ flexGrow: 1, marginLeft: '10px' }}>
                  <Text strong>{fileInfo?.name}</Text>
                  <br />
                  <Text type="secondary">{fileInfo?.size} • Uploading...</Text>
                </div>
                <CustomButton
                  type="text"
                  icon={<CloseOutlined />}
                  onClick={() => cancelUpload()}
                />
              </div>
              <Progress percent={progress} strokeColor="#008080" />
            </div>
          )}
          {error && !uploading && (
            <Alert
              message={error}
              type="error"
              showIcon
              closable
              onClose={() => handleCloseError()}
              style={{ margin: '0px 77px' }}
            />
          )}
        </>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '13px',
            alignItems: 'center',
            padding: '30px',
          }}
        >
          <CheckCircleOutlined style={{ fontSize: '60px', color: '#008080' }} />
          <Title level={3} style={{ marginTop: '16px', color: '#008080' }}>
            Success!
          </Title>
          <Progress
            percent={100}
            status="success"
            strokeColor="#008080"
            style={{ padding: '0px 50px' }}
          />

          <Typography style={{ textAlign: 'center' }}>
            {type == 'Create' ? (
              <>
                Your CSV file has been uploaded and the transactions have been
                <br />
                successfully created in the system.
              </>
            ) : (
              <>
                Your CSV file has been uploaded and the transactions have been
                <br />
                successfully updating in the system, please check in batch
                processes.
              </>
            )}
          </Typography>

          <CustomButton
            shape="round"
            onClick={() => handleClose()}
            style={{
              marginTop: '16px',
              background: '#008080',
              borderColor: '#008080',
            }}
          >
            View Transactions
          </CustomButton>
        </div>
      )}
    </Modal>
  );
};

export default UploadFile;
